import { AdminUpdateProvider } from './app-update.js'
import { RefreshSafetyProvider } from '@speaking-coach/ui'
import { appRefreshSafety } from './app-update-safety.js'
import { ToastProvider } from '@speaking-coach/ui'
import { lazy, Suspense } from 'react'
import { FeedbackListPage } from './feedback/feedback-list-page'
import { FeedbackDetailPage } from './feedback/feedback-detail-page'
import { SessionsListPage } from './sessions/sessions-list-page'
import { SessionDetailPage } from './sessions/session-detail-page'
import { Navigate, Route, Routes } from 'react-router-dom'
import { AdminShell } from './admin-shell'
import { CoooriAdminsListPage } from './admins/admins-list-page'
import { CoooriAdminDetailPage } from './admins/admin-detail-page'
import { CreateCoooriAdminPage } from './admins/create-admin-page'
import { ClientDetailPage } from './clients/client-detail-page'
import { ClientsListPage } from './clients/clients-list-page'
import { CreateClientPage } from './clients/create-client-page'
import { CohortDetailPage } from './cohorts/cohort-detail-page'
import { CohortsListPage } from './cohorts/cohorts-list-page'
import { CreateCohortPage } from './cohorts/create-cohort-page'
import { AuthBootstrap } from './auth/auth-bootstrap'
import { RoleGate } from './auth/role-gate'
import { EmailTestSendPage } from './email_test_send/email-test-send-page'
import { SuppressionDetailPage } from './email_suppressions/suppression-detail-page'
import { SuppressionsListPage } from './email_suppressions/suppressions-list-page'
import { BatchCreateStudentsPage } from './students/batch-create-page'
import { CreateStudentPage } from './students/create-student-page'
import { EditStudentPage } from './students/edit-student-page'
import { StudentsListPage } from './students/students-list-page'
import { CreateHrUserPage } from './hr_users/create-hr-user-page'
import { EditHrUserPage } from './hr_users/edit-hr-user-page'
import { HrUsersListPage } from './hr_users/hr-users-list-page'
import { TranslationsPage } from './translations/translations-page'
import { JobRunsPage } from './jobs/job-runs-page'
import { ContentHistoryPage } from './activity/content-history-page'
import { ActivityPage } from './activity/activity-page'
import { CatalogDetailPage } from './content/catalog-detail-page'
import { CatalogDirectoryPage } from './content/catalog-pages'
import { ContentPage } from './content/curriculum-page'
import { ContentCoveragePage } from './content/coverage-page'
import { ContentTopicPage } from './content/topic-page'
import { ContentDiagnosticPage } from './content/diagnostic-page'
import { ContentAuthoringGuidelinesPage } from './content/authoring-guidelines-page'
import { ContentVocabularyPage } from './content/vocabulary-page'
import { ContentRevisionComparePage, ContentRevisionDetailPage, ContentRevisionListPage } from './content/revision-pages'
import {
  ClientHistoryPage,
  CohortHistoryPage,
  CoooriAdminHistoryPage,
  HrUserHistoryPage,
  StudentHistoryPage,
} from './activity/history-routes'

const DevicesPage = lazy(() => import('./devices/devices-page').then(module => ({default:module.DevicesPage})))

export function App(): JSX.Element {
  return (
    <RefreshSafetyProvider safety={appRefreshSafety}>
      <AdminUpdateProvider>
        <AppRoutes />
      </AdminUpdateProvider>
    </RefreshSafetyProvider>
  )
}

function AppRoutes(): JSX.Element {
  return (
    <AuthBootstrap>
      <RoleGate>
        {/* Above <Routes> deliberately: a toast exists to survive the
            navigation that raised it (ADR-0056), so it cannot be mounted
            inside the route that unmounts. */}
        <ToastProvider>
          <AdminShell>
            <Routes>
              <Route path="/" element={<Navigate to="/admin/students" replace />} />
              <Route path="/login" element={<Navigate to="/admin/students" replace />} />
              <Route path="/admin/translations" element={<TranslationsPage />} />
              <Route path="/admin/content/diagnostics/:programId" element={<ContentDiagnosticPage />} />
              <Route path="/admin/content/revisions/:kind/:identityId/compare" element={<ContentRevisionComparePage />} />
              <Route path="/admin/content/revisions/:kind/:identityId/:revisionId" element={<ContentRevisionDetailPage />} />
              <Route path="/admin/content/revisions/:kind/:identityId" element={<ContentRevisionListPage />} />
              <Route path="/admin/content/programs/:contentId" element={<CatalogDetailPage page="programs" />} />
              <Route path="/admin/content/levels/:contentId" element={<CatalogDetailPage page="levels" />} />
              <Route path="/admin/content/themes/:contentId" element={<CatalogDetailPage page="themes" />} />
              <Route path="/admin/content/tutors/:contentId" element={<CatalogDetailPage page="tutors" />} />
              <Route path="/admin/content/history" element={<ContentHistoryPage />} />
              <Route path="/admin/content" element={<CatalogDirectoryPage page="programs" />} />
              <Route path="/admin/content/levels" element={<CatalogDirectoryPage page="levels" />} />
              <Route path="/admin/content/themes" element={<CatalogDirectoryPage page="themes" />} />
              <Route path="/admin/content/scenarios" element={<ContentPage />} />
              <Route path="/admin/content/tutors" element={<CatalogDirectoryPage page="tutors" />} />
              <Route
                path="/admin/content/coverage"
                element={<ContentCoveragePage />}
              />
              <Route
                path="/admin/content/authoring-guidelines"
                element={<ContentAuthoringGuidelinesPage />}
              />
              <Route
                path="/admin/content/vocabulary"
                element={<ContentVocabularyPage />}
              />
              <Route
                path="/admin/content/scenarios/:topicId"
                element={<ContentTopicPage />}
              />
              <Route path="/admin/email-test-send" element={<EmailTestSendPage />} />
              <Route
                path="/admin/email-suppressions"
                element={<SuppressionsListPage />}
              />
              <Route
                path="/admin/email-suppressions/:emailHash"
                element={<SuppressionDetailPage />}
              />
              <Route path="/admin/jobs" element={<JobRunsPage />} />
              <Route path="/admin/feedback" element={<FeedbackListPage />} />
              <Route path="/admin/feedback/:feedbackId" element={<FeedbackDetailPage />} />
              <Route path="/admin/sessions" element={<SessionsListPage />} />
              <Route path="/admin/devices" element={<Suspense fallback={<p role="status" className="p-6">Loading recorded environments…</p>}><DevicesPage /></Suspense>} />
              <Route path="/admin/sessions/:sessionId" element={<SessionDetailPage />} />
              <Route path="/admin/activity" element={<ActivityPage />} />
              {/* Each provisioning detail page has a sibling History
                  route (PROV-AUDIT) — the router is the mode switch. */}
              <Route
                path="/admin/clients/:clientId/history"
                element={<ClientHistoryPage />}
              />
              <Route
                path="/admin/clients/:clientId/cohorts/:cohortId/history"
                element={<CohortHistoryPage />}
              />
              <Route
                path="/admin/students/:userId/history"
                element={<StudentHistoryPage />}
              />
              <Route
                path="/admin/clients/:clientId/hr-users/:userId/history"
                element={<HrUserHistoryPage />}
              />
              <Route
                path="/admin/admins/:userId/history"
                element={<CoooriAdminHistoryPage />}
              />
              {/* Clients before students: an operator creates the tenant,
                  then adds people to it. */}
              <Route path="/admin/clients" element={<ClientsListPage />} />
              <Route path="/admin/clients/new" element={<CreateClientPage />} />
              <Route
                path="/admin/clients/:clientId"
                element={<ClientDetailPage />}
              />
              <Route
                path="/admin/clients/:clientId/hr-users/new"
                element={<CreateHrUserPage />}
              />
              <Route
                path="/admin/clients/:clientId/hr-users/:userId"
                element={<EditHrUserPage />}
              />
              {/* Global cohort list — the cohort navigation entrypoint, with
                  the client as a filterable column (ADR-0052 D1). Creating one
                  still happens from a client: reads global, writes targeted. */}
              <Route path="/admin/cohorts" element={<CohortsListPage />} />
              {/* `cohorts/new` before `cohorts/:cohortId` so the literal wins
                  over the param — react-router ranks static segments higher,
                  but the explicit order documents the intent. */}
              <Route
                path="/admin/clients/:clientId/cohorts/new"
                element={<CreateCohortPage />}
              />
              <Route
                path="/admin/clients/:clientId/cohorts/:cohortId"
                element={<CohortDetailPage />}
              />
              <Route path="/admin/students" element={<StudentsListPage />} />
              <Route path="/admin/hr-users" element={<HrUsersListPage />} />
              <Route path="/admin/admins" element={<CoooriAdminsListPage />} />
              <Route
                path="/admin/admins/new"
                element={<CreateCoooriAdminPage />}
              />
              <Route
                path="/admin/admins/:userId"
                element={<CoooriAdminDetailPage />}
              />
              <Route path="/admin/students/new" element={<CreateStudentPage />} />
              {/* Both create forms are global with ?clientId= context —
                  the batch page's client picker is step one of its flow. */}
              <Route
                path="/admin/students/batch"
                element={<BatchCreateStudentsPage />}
              />
              <Route path="/admin/students/:userId/edit" element={<EditStudentPage />} />
              <Route path="*" element={<Navigate to="/admin/students" replace />} />
            </Routes>
          </AdminShell>
        </ToastProvider>
      </RoleGate>
    </AuthBootstrap>
  )
}
